import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    Send, Store, TrendingUp, GraduationCap, Package, Megaphone,
    BadgeCheck, MapPin, ChevronRight,
} from "lucide-react";
import { BRAND, COMPANY, BRANCHES } from "../lib/bakeryData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADVANTAGES = [
    {
        icon: BadgeCheck,
        title: "A proven, working model",
        text: "Three branches running profitably across Chhattisgarh since 2023 — the menu, pricing and daily playbook are already battle-tested.",
    },
    {
        icon: Store,
        title: "Low-cost, high-return setup",
        text: "A compact format that works in small towns — modest investment, quick break-even, and a product mix engineered for daily repeat purchases.",
    },
    {
        icon: Package,
        title: "Central supply & recipes",
        text: "Standardised recipes, ingredients sourcing guidance, and supply-chain support so every branch tastes exactly like Ambikapur.",
    },
    {
        icon: GraduationCap,
        title: "Full training for your team",
        text: "Hands-on training for bakers and front-of-house staff at our Ambikapur kitchen before your launch day.",
    },
    {
        icon: Megaphone,
        title: "Marketing & launch support",
        text: "Grand-opening planning, social media creatives, and the brand's existing Instagram presence (@thebakers_basket) to announce you.",
    },
    {
        icon: TrendingUp,
        title: "Growing regional brand",
        text: "An active Zomato & Swiggy presence, 1000+ bakes sold daily, and a customer base that already knows the name.",
    },
];

const BUDGETS = [
    "Under ₹10 Lakhs",
    "₹10 – 20 Lakhs",
    "₹20 – 35 Lakhs",
    "₹35 Lakhs and above",
];

const Franchise = () => {
    const [form, setForm] = useState({
        name: "", phone: "", email: "", city: "", budget: "", space: "", message: "",
    });
    const [loading, setLoading] = useState(false);
    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.city.trim()) {
            toast.error("Please fill in your name, phone, email and city.");
            return;
        }
        try {
            setLoading(true);
            await axios.post(`${API}/franchise`, form);
            toast.success("Thank you! Your franchise enquiry has been sent to our team.");
            setForm({ name: "", phone: "", email: "", city: "", budget: "", space: "", message: "" });
        } catch (err) {
            toast.error("Couldn't send your enquiry. Please try again or email us directly.");
        } finally {
            setLoading(false);
        }
    };

    const inputCls = "mt-2 w-full px-4 py-3 rounded-xl border border-[#E8D8C8] bg-[#FAF8F5] text-[#3E2723] focus:outline-none focus:border-[#C5832B] focus:ring-2 focus:ring-[#C5832B]/20 transition-all";

    return (
        <main data-testid="franchise-page" className="bg-[#FAF8F5] pt-32 pb-20">
            {/* Hero */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 text-center">
                <p className="font-handwriting text-3xl text-[#C5832B]">Own a bakery</p>
                <h1 className="font-serif text-5xl md:text-6xl text-[#3E2723] mt-3 leading-[1.05]">
                    Bring The Baker's Basket<br />to <span className="italic text-[#C5832B]">your town</span>.
                </h1>
                <p className="text-base md:text-lg text-[#6D4C41] mt-6 max-w-2xl mx-auto leading-relaxed">
                    In three years we've grown from one oven in Ambikapur to three busy
                    branches. Now we're opening the doors to partners who share our
                    obsession with butter, bread and community.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                    <a
                        href="#franchise-form"
                        data-testid="franchise-hero-cta"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#3E2723] text-[#FAF8F5] text-sm hover:bg-[#C5832B] transition-all"
                    >
                        Fill the franchise form
                        <ChevronRight className="w-4 h-4" />
                    </a>
                    <a
                        href="#franchise-advantages"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#3E2723]/20 text-[#3E2723] text-sm hover:bg-[#3E2723] hover:text-[#FAF8F5] transition-all"
                    >
                        See the advantages
                    </a>
                </div>
            </section>

            {/* About Franchise */}
            <section id="franchise-about" data-testid="franchise-about-section" className="max-w-7xl mx-auto px-6 md:px-10 mt-24 scroll-mt-28">
                <div className="grid lg:grid-cols-2 gap-12 items-center bg-[#F3EBE1] border border-[#E8D8C8] rounded-[2rem] p-8 md:p-14">
                    <div>
                        <p className="font-handwriting text-3xl text-[#C5832B]">About the franchise</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3 leading-tight">
                            A small-town format that actually works
                        </h2>
                        <p className="text-base text-[#6D4C41] mt-6 leading-relaxed">
                            {BRAND.name} began on <strong>{BRAND.foundedOn}</strong> in Ambikapur
                            — a town everyone said was "too small" for world-class bread.
                            Today our three branches (Ambikapur, Surajpur, Bishrampur) bake
                            1000+ items a day, powered by fresh morning production, a café
                            counter, and online delivery.
                        </p>
                        <p className="text-base text-[#6D4C41] mt-4 leading-relaxed">
                            As a franchise partner, you get the complete system — recipes,
                            training, branding, supply chain and marketing — plus a
                            relationship directly with the founders, not a call centre.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                        {[
                            { v: "3", l: "Branches live" },
                            { v: "1000+", l: "Bakes every day" },
                            { v: "20+", l: "Menu categories" },
                            { v: "3 yrs", l: "Proven in market" },
                        ].map((s) => (
                            <div key={s.l} className="bg-white border border-[#E8D8C8] rounded-2xl p-6 text-center">
                                <p className="font-serif text-4xl text-[#3E2723]">{s.v}</p>
                                <p className="text-xs uppercase tracking-[0.16em] text-[#6D4C41] mt-2">{s.l}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Advantages */}
            <section id="franchise-advantages" data-testid="franchise-advantages-section" className="max-w-7xl mx-auto px-6 md:px-10 mt-24 scroll-mt-28">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <p className="font-handwriting text-3xl text-[#C5832B]">Why partner with us</p>
                    <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3">
                        The franchise advantages
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {ADVANTAGES.map((a, i) => (
                        <article
                            key={a.title}
                            data-testid={`franchise-advantage-${i}`}
                            className="bg-white border border-[#E8D8C8] rounded-3xl p-8 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(62,39,35,0.3)] transition-all duration-500"
                        >
                            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#C5832B]/12 text-[#C5832B]">
                                <a.icon className="w-5 h-5" />
                            </span>
                            <h3 className="font-serif text-2xl text-[#3E2723] mt-5">{a.title}</h3>
                            <p className="text-sm text-[#6D4C41] mt-3 leading-relaxed">{a.text}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* Form */}
            <section id="franchise-form" data-testid="franchise-form-section" className="max-w-7xl mx-auto px-6 md:px-10 mt-24 scroll-mt-28">
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <p className="font-handwriting text-3xl text-[#C5832B]">Let's talk</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3 leading-tight">
                            Fill the franchise form
                        </h2>
                        <p className="text-base text-[#6D4C41] mt-5 leading-relaxed">
                            Share a few details about yourself and your city. Your enquiry
                            goes straight to the founders' inbox — we typically respond
                            within 2–3 working days.
                        </p>
                        <div className="mt-7 space-y-3 text-sm text-[#6D4C41]">
                            <p className="flex items-start gap-2.5">
                                <MapPin className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                <span>
                                    <strong className="text-[#3E2723]">Corporate office:</strong>{" "}
                                    {COMPANY.corporateOffice}
                                </span>
                            </p>
                            <p className="flex items-start gap-2.5">
                                <Send className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                <span>
                                    <strong className="text-[#3E2723]">Franchise desk:</strong>{" "}
                                    {COMPANY.franchiseEmail}
                                </span>
                            </p>
                        </div>
                        <p className="text-xs text-[#6D4C41]/70 mt-6 leading-relaxed">
                            Prefer a conversation? Call us at {COMPANY.customerCare.phone}.
                        </p>
                    </div>

                    <form
                        onSubmit={submit}
                        data-testid="franchise-form"
                        className="lg:col-span-7 bg-white border border-[#E8D8C8] rounded-3xl p-8 md:p-10"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">Your name *</label>
                                <input type="text" required value={form.name} onChange={update("name")}
                                    data-testid="franchise-input-name" placeholder="Full name" className={inputCls} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">Phone *</label>
                                <input type="tel" required value={form.phone} onChange={update("phone")}
                                    data-testid="franchise-input-phone" placeholder="+91 ..." className={inputCls} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">Email *</label>
                                <input type="email" required value={form.email} onChange={update("email")}
                                    data-testid="franchise-input-email" placeholder="you@email.com" className={inputCls} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">City / Town *</label>
                                <input type="text" required value={form.city} onChange={update("city")}
                                    data-testid="franchise-input-city" placeholder="Where would you open?" className={inputCls} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">Investment budget</label>
                                <select value={form.budget} onChange={update("budget")}
                                    data-testid="franchise-input-budget" className={inputCls}>
                                    <option value="">Select a range</option>
                                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">Space available</label>
                                <input type="text" value={form.space} onChange={update("space")}
                                    data-testid="franchise-input-space" placeholder="e.g. 800 sq ft" className={inputCls} />
                            </div>
                        </div>
                        <div className="mt-5">
                            <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">Anything you'd like us to know</label>
                            <textarea rows={4} value={form.message} onChange={update("message")}
                                data-testid="franchise-input-message"
                                placeholder="Your background, location details, timeline…"
                                className={`${inputCls} resize-none`} />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="franchise-submit-btn"
                            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#3E2723] text-[#FAF8F5] text-sm hover:bg-[#C5832B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? "Sending..." : "Submit franchise enquiry"}
                            <Send className="w-4 h-4" />
                        </button>
                        <p className="text-xs text-[#6D4C41]/70 mt-4">
                            By submitting, you agree to be contacted by {COMPANY.firmName} regarding franchise opportunities.
                        </p>
                    </form>
                </div>
            </section>
        </main>
    );
};

export default Franchise;
